import React, { useState } from 'react';

function PmsPropertyPnl({ token, selectedProperty }) {
  const today = new Date().toISOString().slice(0, 10);
  const monthStart = today.slice(0, 8) + '01';
  const [from, setFrom] = useState(monthStart);
  const [to, setTo] = useState(today);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const headers = (extra = {}) => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || '',
    ...extra
  });

  const run = async () => {
    if (!selectedProperty?.id) return setError('Select property first');
    setLoading(true);
    setError('');
    setData(null);
    try {
      const qs = new URLSearchParams({ from, to });
      const res = await fetch(`/api/v1/expense/reports/property-pnl?${qs.toString()}`, { headers: headers() });
      const json = await res.json();
      if (!res.ok) return setError(json.error || 'Run failed');
      setData(json);
    } catch { setError('Network error'); }
    finally { setLoading(false); }
  };

  const downloadPdf = async () => {
    setError('');
    try {
      const qs = new URLSearchParams({ from, to, format: 'pdf' });
      const res = await fetch(`/api/v1/expense/reports/property-pnl?${qs.toString()}`, { headers: headers() });
      if (!res.ok) { const j = await res.json().catch(() => ({})); return setError(j.error || 'PDF failed'); }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `property-pnl-${from}-to-${to}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch { setError('Network error'); }
  };

  const fmtMoney = (n) => `₹ ${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtKpi = (v) => {
    if (typeof v === 'string') return v;
    if (typeof v === 'number') return v < 1000 ? Number(v).toFixed(2) : fmtMoney(v);
    return '—';
  };
  const inputStyle = { padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.85rem' };

  const kpiTile = (label, value, accent = '#3b82f6') => (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderLeft: `4px solid ${accent}`, borderRadius: 8, padding: '0.7rem 1rem', minWidth: 130, flex: '1 1 130px' }}>
      <div style={{ color: '#6b7280', fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' }}>{label}</div>
      <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#111827', marginTop: 4 }}>{value}</div>
    </div>
  );

  // Section row renderer — different styles per row type
  const renderRow = (s, i) => {
    if (s.type === 'SECTION_HEAD') {
      return (
        <tr key={i} style={{ background: '#1f2937' }}>
          <td colSpan={2} style={{ padding: '0.55rem 0.8rem', color: '#fff', fontWeight: 700, letterSpacing: '0.04em', fontSize: '0.78rem', textTransform: 'uppercase' }}>{s.label}</td>
        </tr>
      );
    }
    if (s.type === 'SECTION_TOTAL') {
      return (
        <tr key={i} style={{ background: '#f3f4f6', borderTop: '1px solid #d1d5db' }}>
          <td style={{ padding: '0.5rem 0.8rem', fontWeight: 700 }}>{s.label}</td>
          <td style={{ padding: '0.5rem 0.8rem', textAlign: 'right', fontWeight: 700 }}>{fmtMoney(s.amount)}</td>
        </tr>
      );
    }
    if (s.type === 'GROSS_TOTAL') {
      const positive = Number(s.amount) >= 0;
      return (
        <tr key={i} style={{ background: positive ? '#dcfce7' : '#fee2e2', borderTop: '2px solid', borderBottom: '2px solid', borderColor: positive ? '#16a34a' : '#dc2626' }}>
          <td style={{ padding: '0.6rem 0.8rem', fontWeight: 800, color: positive ? '#166534' : '#991b1b' }}>
            {s.label}{s.pct && <span style={{ marginLeft: '0.6rem', fontSize: '0.8rem', fontWeight: 600, opacity: 0.8 }}>({s.pct})</span>}
          </td>
          <td style={{ padding: '0.6rem 0.8rem', textAlign: 'right', fontWeight: 800, color: positive ? '#166534' : '#991b1b', fontSize: '1rem' }}>{fmtMoney(s.amount)}</td>
        </tr>
      );
    }
    // Default LINE
    const isNeg = Number(s.amount) < 0;
    return (
      <tr key={i} style={{ borderTop: '1px solid #f3f4f6' }}>
        <td style={{ padding: '0.45rem 0.8rem 0.45rem 1.6rem', color: '#374151' }}>{s.label}</td>
        <td style={{ padding: '0.45rem 0.8rem', textAlign: 'right', color: isNeg ? '#dc2626' : '#111827' }}>{fmtMoney(s.amount)}</td>
      </tr>
    );
  };

  return (
    <div className="page-shell">
      <h2 style={{ color: 'var(--theme-primary-dark)', marginBottom: '0.8rem' }}>Property P&L Statement</h2>
      {!selectedProperty?.id && <div className="error-banner">Select a property first.</div>}
      {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '0.7rem 1rem', borderRadius: 6, marginBottom: '0.8rem', borderLeft: '4px solid #dc2626' }}>⚠ {error}</div>}

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '1rem', marginBottom: '0.8rem', display: 'flex', gap: '0.6rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <label style={{ display: 'grid', gap: '0.2rem', fontSize: '0.78rem' }}><span>From</span><input type="date" value={from} onChange={e => setFrom(e.target.value)} style={inputStyle} /></label>
        <label style={{ display: 'grid', gap: '0.2rem', fontSize: '0.78rem' }}><span>To</span><input type="date" value={to} onChange={e => setTo(e.target.value)} style={inputStyle} /></label>
        <button onClick={run} disabled={loading} style={{ padding: '0.5rem 1rem', border: 'none', borderRadius: 6, background: '#2563eb', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>{loading ? 'Computing…' : 'Generate P&L'}</button>
        <button onClick={downloadPdf} disabled={!data} style={{ padding: '0.5rem 1rem', border: '1px solid #d1d5db', borderRadius: 6, background: data ? '#fff' : '#f9fafb', cursor: data ? 'pointer' : 'not-allowed', color: data ? '#374151' : '#9ca3af' }}>Download PDF</button>
      </div>

      {data ? (
        <>
          {/* KPI strip */}
          {Array.isArray(data.kpis) && (
            <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', marginBottom: '0.8rem' }}>
              {data.kpis.map((k, i) => {
                const colors = ['#3b82f6','#0ea5e9','#16a34a','#8b5cf6','#f59e0b','#ef4444','#10b981'];
                return kpiTile(k.label, fmtKpi(k.value), colors[i % colors.length]);
              })}
            </div>
          )}

          {/* P&L sections */}
          {Array.isArray(data.sections) && (
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
                <tbody>
                  {data.sections.map(renderRow)}
                </tbody>
              </table>
            </div>
          )}

          <div style={{ marginTop: '0.8rem', fontSize: '0.78rem', color: '#6b7280', padding: '0.6rem 1rem', background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 6 }}>
            ⚠ <strong>Depreciation excluded from this P&L.</strong> Auto-depreciation from asset_master is a separate workstream. Tax (PAT) computation also out of scope in v1 — PBT = EBITDA + non-operating items.
          </div>
        </>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
          Pick a date range and click <strong>Generate P&L</strong>. Revenue aggregates room charges (folio), F&B + Bar (orders), Banquet (banquet_payment), and other folio income, all net of GST. Expenses are summed from APPROVED/POSTED/PAID entries, with ITC-eligible tax excluded from cost (per SRS §4.9.4.2 B1 formula).
        </div>
      )}
    </div>
  );
}

export default PmsPropertyPnl;
